import type { DropResult } from '@hello-pangea/dnd'
import { DragDropContext, Draggable, Droppable } from '@hello-pangea/dnd'
import { TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import * as R from 'ramda'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import { useElementRootStyles } from 'components/Editor/EditorElements/hooks/useElementRootStyles'
import {
  getFeedbackValue,
  onChangeRTGeneralFb,
} from 'components/Editor/EditorElements/utils/getFeedbackValue'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'

import type { TestSingleChoiceElementType } from '../testSingleChoiceTypes'
import TestSingleChoiceElementEditorItem from './TestSingleChoiceElementEditorItem'

const TestSingleChoiceElementEditor: TestSingleChoiceElementType = ({
  element,
  mode,
  styles,
  waiting,
  font,
  feedback,
  onChange,
  state,
}) => {
  const { id: elementId, value } = element
  const { generalFbValid } = state || {}
  const feedbackValue = getFeedbackValue(value)

  const rootStyles = useElementRootStyles(styles.root)

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })

  const handleMove = ({ destination, source }: DropResult) =>
    destination &&
    onChange?.(R.assoc('items', R.move(source.index, destination.index, value.items), value))

  return (
    <div style={rootStyles}>
      <DragDropContext onDragEnd={handleMove}>
        <Droppable
          direction='vertical'
          droppableId={elementId}
          renderClone={(provided, _snapshot, { source }) => (
            <TestSingleChoiceElementEditorItem
              element={element}
              feedback={feedback}
              font={font}
              index={source.index}
              mode={mode}
              onChange={onChange}
              provided={provided}
              styles={styles}
              waiting={waiting}
            />
          )}
        >
          {(provided, { isDraggingOver }) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {value.items.map((item, index) => {
                return (
                  <Draggable draggableId={item.value} index={index} key={item.value}>
                    {(provided) => (
                      <TestSingleChoiceElementEditorItem
                        element={element}
                        feedback={feedback}
                        font={font}
                        index={index}
                        mode={mode}
                        onChange={onChange}
                        provided={provided}
                        showControls={!isDraggingOver}
                        styles={styles}
                        waiting={waiting}
                      />
                    )}
                  </Draggable>
                )
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={generalFbValid}
        mode={mode}
        onChange={onChangeRTGeneralFb(value, onChange)}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: value.generalFeedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.perOption
        }
        styles={feedback}
      />
    </div>
  )
}

export default TestSingleChoiceElementEditor
